import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  IndianRupee, 
  Plus, 
  Percent, 
  Calendar,
  TrendingUp,
  Building2,
  BookOpen,
  Clock,
  CheckCircle,
  AlertCircle,
  CreditCard,
  Banknote,
  Download,
  RefreshCw,
  Tag,
  Gift
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeeManagement = () => {
  const [activeTab, setActiveTab] = useState('revenue');
  const [loading, setLoading] = useState(true);
  
  // Revenue Dashboard State
  const [revenueDashboard, setRevenueDashboard] = useState(null);
  
  // Fee Structures State
  const [feeStructures, setFeeStructures] = useState([]);
  const [courses, setCourses] = useState([]);
  const [feeDialogOpen, setFeeDialogOpen] = useState(false);
  const [feeForm, setFeeForm] = useState({
    course_id: '',
    course_name: '',
    total_fee: '',
    installment_enabled: true,
    installment_1_percent: 60,
    installment_2_percent: 40,
    installment_1_due_days: 0,
    installment_2_due_days: 30,
    late_fee_per_day: 10
  });
  
  // Discounts State
  const [discounts, setDiscounts] = useState([]);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountForm, setDiscountForm] = useState({
    name: '',
    discount_type: 'early_bird',
    discount_percent: '',
    discount_amount: '',
    valid_from: '',
    valid_until: '',
    applicable_courses: []
  });
  
  // Student Fees State
  const [studentFees, setStudentFees] = useState([]);
  const [feeFilter, setFeeFilter] = useState('all');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [revenueRes, structuresRes, coursesRes, discountsRes, feesRes] = await Promise.all([
        axios.get(`${API}/admin/revenue-dashboard`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/fee-structures`).catch(() => ({ data: [] })),
        axios.get(`${API}/courses`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/discounts`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/student-fees`).catch(() => ({ data: [] }))
      ]);
      
      setRevenueDashboard(revenueRes.data);
      setFeeStructures(structuresRes.data || []);
      setCourses(coursesRes.data || []);
      setDiscounts(discountsRes.data || []);
      setStudentFees(feesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fee Structure handlers
  const handleCourseSelect = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    if (course) {
      setFeeForm({
        ...feeForm,
        course_id: course.id,
        course_name: course.name,
        total_fee: course.fee || ''
      });
    }
  };

  const handleCreateFeeStructure = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/fee-structures`, {
        ...feeForm,
        total_fee: parseFloat(feeForm.total_fee),
        late_fee_per_day: parseFloat(feeForm.late_fee_per_day)
      });
      toast.success('Fee structure created/updated');
      setFeeDialogOpen(false);
      setFeeForm({
        course_id: '',
        course_name: '',
        total_fee: '',
        installment_enabled: true,
        installment_1_percent: 60,
        installment_2_percent: 40,
        installment_1_due_days: 0,
        installment_2_due_days: 30,
        late_fee_per_day: 10
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create fee structure');
    }
  };

  // Discount handlers
  const handleCreateDiscount = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/discounts`, {
        ...discountForm,
        discount_percent: discountForm.discount_percent ? parseFloat(discountForm.discount_percent) : 0,
        discount_amount: discountForm.discount_amount ? parseFloat(discountForm.discount_amount) : 0
      });
      toast.success('Discount created');
      setDiscountDialogOpen(false);
      setDiscountForm({
        name: '',
        discount_type: 'early_bird',
        discount_percent: '',
        discount_amount: '',
        valid_from: '',
        valid_until: '',
        applicable_courses: []
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create discount');
    }
  };

  const handleToggleDiscount = async (discountId) => {
    try {
      await axios.put(`${API}/admin/discounts/${discountId}/toggle`);
      toast.success('Discount status updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update discount');
    }
  };

  const filteredFees = studentFees.filter(fee => {
    if (feeFilter === 'all') return true;
    return fee.status === feeFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <IndianRupee className="h-6 w-6 text-emerald-600" />
          Fee Management System
        </h2>
        <Button onClick={fetchAllData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="revenue" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <TrendingUp className="h-4 w-4 mr-2" /> Revenue Dashboard
          </TabsTrigger>
          <TabsTrigger value="structures" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <BookOpen className="h-4 w-4 mr-2" /> Fee Structures
          </TabsTrigger>
          <TabsTrigger value="discounts" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
            <Tag className="h-4 w-4 mr-2" /> Discounts
          </TabsTrigger>
          <TabsTrigger value="fees" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
            <CreditCard className="h-4 w-4 mr-2" /> Student Fees
          </TabsTrigger>
        </TabsList>

        {/* Revenue Dashboard Tab */}
        <TabsContent value="revenue" className="space-y-6">
          {revenueDashboard ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-100 text-sm">Total Revenue</p>
                      <p className="text-3xl font-bold">₹{revenueDashboard.summary.total_revenue?.toLocaleString()}</p>
                    </div>
                    <IndianRupee className="h-10 w-10 text-emerald-200" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Monthly Revenue</p>
                      <p className="text-3xl font-bold">₹{revenueDashboard.summary.monthly_revenue?.toLocaleString()}</p>
                    </div>
                    <Calendar className="h-10 w-10 text-blue-200" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-100 text-sm">Pending Fees</p>
                      <p className="text-3xl font-bold">₹{revenueDashboard.summary.total_pending?.toLocaleString()}</p>
                    </div>
                    <Clock className="h-10 w-10 text-amber-200" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-5 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-red-100 text-sm">Late Fee Collected</p>
                      <p className="text-3xl font-bold">₹{revenueDashboard.summary.total_late_fee?.toLocaleString()}</p>
                    </div>
                    <AlertCircle className="h-10 w-10 text-red-200" />
                  </div>
                </div>
              </div>

              {/* Payment Method & Installment Breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Payment Methods */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-bold text-slate-800 mb-4">Payment Methods</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CreditCard className="h-5 w-5 text-blue-600" />
                        <span className="font-medium">Online Payments</span>
                      </div>
                      <span className="font-bold text-blue-700">₹{revenueDashboard.summary.online_revenue?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Banknote className="h-5 w-5 text-green-600" />
                        <span className="font-medium">Cash Collection</span>
                      </div>
                      <span className="font-bold text-green-700">₹{revenueDashboard.summary.cash_revenue?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Installment Breakdown */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-bold text-slate-800 mb-4">Installment Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">1st Installment (60%)</span>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">₹{revenueDashboard.installment_breakdown.installment_1_collected?.toLocaleString()}</p>
                        <p className="text-xs text-slate-400">Pending: ₹{revenueDashboard.installment_breakdown.installment_1_pending?.toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">2nd Installment (40%)</span>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">₹{revenueDashboard.installment_breakdown.installment_2_collected?.toLocaleString()}</p>
                        <p className="text-xs text-slate-400">Pending: ₹{revenueDashboard.installment_breakdown.installment_2_pending?.toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="pt-2 border-t">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-4 w-4" /> Overdue Students
                        </span>
                        <span className="font-bold text-red-600">{revenueDashboard.summary.overdue_count}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Center-wise & Course-wise Revenue */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Center-wise Revenue */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-blue-600" />
                    Center-wise Revenue
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {revenueDashboard.center_wise_revenue?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                        <span className="text-sm text-slate-700">{item.center_name}</span>
                        <span className="font-medium text-emerald-600">₹{item.revenue?.toLocaleString()}</span>
                      </div>
                    ))}
                    {(!revenueDashboard.center_wise_revenue || revenueDashboard.center_wise_revenue.length === 0) && (
                      <p className="text-slate-400 text-center py-4">No data available</p>
                    )}
                  </div>
                </div>

                {/* Course-wise Revenue */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-purple-600" />
                    Course-wise Revenue
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {revenueDashboard.course_wise_revenue?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                        <span className="text-sm text-slate-700">{item.course_name}</span>
                        <span className="font-medium text-purple-600">₹{item.revenue?.toLocaleString()}</span>
                      </div>
                    ))}
                    {(!revenueDashboard.course_wise_revenue || revenueDashboard.course_wise_revenue.length === 0) && (
                      <p className="text-slate-400 text-center py-4">No data available</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Payments */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-bold text-slate-800 mb-4">Recent Payments</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3">Student</th>
                        <th className="text-left p-3">Course</th>
                        <th className="text-left p-3">Installment</th>
                        <th className="text-left p-3">Amount</th>
                        <th className="text-left p-3">Method</th>
                        <th className="text-left p-3">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueDashboard.recent_payments?.slice(0, 10).map((payment, idx) => (
                        <tr key={idx} className="border-t hover:bg-slate-50">
                          <td className="p-3">{payment.student_name}</td>
                          <td className="p-3">{payment.course_name}</td>
                          <td className="p-3">#{payment.installment_number}</td>
                          <td className="p-3 font-medium text-emerald-600">₹{payment.total_amount?.toLocaleString()}</td>
                          <td className="p-3">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              payment.payment_method === 'online' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {payment.payment_method}
                            </span>
                          </td>
                          <td className="p-3 text-slate-500">
                            {new Date(payment.payment_date).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(!revenueDashboard.recent_payments || revenueDashboard.recent_payments.length === 0) && (
                    <p className="text-slate-400 text-center py-8">No payments yet</p>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <TrendingUp className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>No revenue data available yet</p>
            </div>
          )}
        </TabsContent>

        {/* Fee Structures Tab */}
        <TabsContent value="structures" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-slate-800">Course Fee Structures</h3>
            <Dialog open={feeDialogOpen} onOpenChange={setFeeDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" /> Add Fee Structure
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create/Update Fee Structure</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateFeeStructure} className="space-y-4">
                  <div>
                    <Label>Select Course</Label>
                    <select
                      className="w-full mt-1 p-2 border rounded-lg"
                      value={feeForm.course_id}
                      onChange={(e) => handleCourseSelect(e.target.value)}
                      required
                    >
                      <option value="">Select a course...</option>
                      {courses.map(course => (
                        <option key={course.id} value={course.id}>{course.name} - ₹{course.fee}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Total Fee (₹)</Label>
                    <Input
                      type="number"
                      value={feeForm.total_fee}
                      onChange={(e) => setFeeForm({...feeForm, total_fee: e.target.value})}
                      required
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="installment_enabled"
                      checked={feeForm.installment_enabled}
                      onChange={(e) => setFeeForm({...feeForm, installment_enabled: e.target.checked})}
                    />
                    <Label htmlFor="installment_enabled">Enable Installments</Label>
                  </div>
                  {feeForm.installment_enabled && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>1st Installment %</Label>
                          <Input
                            type="number"
                            value={feeForm.installment_1_percent}
                            onChange={(e) => setFeeForm({...feeForm, installment_1_percent: parseInt(e.target.value)})}
                          />
                        </div>
                        <div>
                          <Label>2nd Installment %</Label>
                          <Input
                            type="number"
                            value={feeForm.installment_2_percent}
                            onChange={(e) => setFeeForm({...feeForm, installment_2_percent: parseInt(e.target.value)})}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>1st Due (days)</Label>
                          <Input
                            type="number"
                            value={feeForm.installment_1_due_days}
                            onChange={(e) => setFeeForm({...feeForm, installment_1_due_days: parseInt(e.target.value)})}
                          />
                        </div>
                        <div>
                          <Label>2nd Due (days)</Label>
                          <Input
                            type="number"
                            value={feeForm.installment_2_due_days}
                            onChange={(e) => setFeeForm({...feeForm, installment_2_due_days: parseInt(e.target.value)})}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div>
                    <Label>Late Fee (₹/day)</Label>
                    <Input
                      type="number"
                      value={feeForm.late_fee_per_day}
                      onChange={(e) => setFeeForm({...feeForm, late_fee_per_day: parseFloat(e.target.value)})}
                    />
                  </div>
                  <Button type="submit" className="w-full">Save Fee Structure</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {feeStructures.map(structure => (
              <div key={structure.id} className="bg-white border rounded-xl p-4">
                <h4 className="font-bold text-slate-800">{structure.course_name}</h4>
                <p className="text-2xl font-bold text-emerald-600 mt-2">₹{structure.total_fee?.toLocaleString()}</p>
                {structure.installment_enabled && (
                  <div className="mt-3 space-y-1 text-sm text-slate-600">
                    <p>1st: {structure.installment_1_percent}% (Due: Day {structure.installment_1_due_days})</p>
                    <p>2nd: {structure.installment_2_percent}% (Due: Day {structure.installment_2_due_days})</p>
                    <p className="text-red-500">Late Fee: ₹{structure.late_fee_per_day}/day</p>
                  </div>
                )}
              </div>
            ))}
            {feeStructures.length === 0 && (
              <div className="col-span-full text-center py-8 text-slate-400">
                No fee structures configured yet
              </div>
            )}
          </div>
        </TabsContent>

        {/* Discounts Tab */}
        <TabsContent value="discounts" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-slate-800">Discount & Scholarships</h3>
            <Dialog open={discountDialogOpen} onOpenChange={setDiscountDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="h-4 w-4 mr-2" /> Add Discount
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create Discount</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateDiscount} className="space-y-4">
                  <div>
                    <Label>Discount Name</Label>
                    <Input
                      value={discountForm.name}
                      onChange={(e) => setDiscountForm({...discountForm, name: e.target.value})}
                      placeholder="e.g., Early Bird Discount"
                      required
                    />
                  </div>
                  <div>
                    <Label>Discount Type</Label>
                    <select
                      className="w-full mt-1 p-2 border rounded-lg"
                      value={discountForm.discount_type}
                      onChange={(e) => setDiscountForm({...discountForm, discount_type: e.target.value})}
                    >
                      <option value="early_bird">Early Bird</option>
                      <option value="merit">Merit Scholarship</option>
                      <option value="festival">Festival Discount</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Discount %</Label>
                      <Input
                        type="number"
                        value={discountForm.discount_percent}
                        onChange={(e) => setDiscountForm({...discountForm, discount_percent: e.target.value})}
                        placeholder="e.g., 10"
                      />
                    </div>
                    <div>
                      <Label>OR Fixed Amount (₹)</Label>
                      <Input
                        type="number"
                        value={discountForm.discount_amount}
                        onChange={(e) => setDiscountForm({...discountForm, discount_amount: e.target.value})}
                        placeholder="e.g., 500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Valid From</Label>
                      <Input
                        type="date"
                        value={discountForm.valid_from}
                        onChange={(e) => setDiscountForm({...discountForm, valid_from: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Valid Until</Label>
                      <Input
                        type="date"
                        value={discountForm.valid_until}
                        onChange={(e) => setDiscountForm({...discountForm, valid_until: e.target.value})}
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full">Create Discount</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {discounts.map(discount => (
              <div key={discount.id} className={`border rounded-xl p-4 ${discount.is_active ? 'bg-white' : 'bg-slate-50 opacity-60'}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      discount.discount_type === 'early_bird' ? 'bg-blue-100 text-blue-700' :
                      discount.discount_type === 'merit' ? 'bg-green-100 text-green-700' :
                      discount.discount_type === 'festival' ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {discount.discount_type.replace('_', ' ').toUpperCase()}
                    </span>
                    <h4 className="font-bold text-slate-800 mt-2">{discount.name}</h4>
                  </div>
                  <Gift className={`h-6 w-6 ${discount.is_active ? 'text-purple-500' : 'text-slate-300'}`} />
                </div>
                <p className="text-2xl font-bold text-purple-600 mt-2">
                  {discount.discount_percent > 0 ? `${discount.discount_percent}%` : `₹${discount.discount_amount}`}
                </p>
                {discount.valid_until && (
                  <p className="text-xs text-slate-500 mt-2">
                    Valid until: {new Date(discount.valid_until).toLocaleDateString()}
                  </p>
                )}
                <Button
                  variant={discount.is_active ? "outline" : "default"}
                  size="sm"
                  className="mt-3 w-full"
                  onClick={() => handleToggleDiscount(discount.id)}
                >
                  {discount.is_active ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            ))}
            {discounts.length === 0 && (
              <div className="col-span-full text-center py-8 text-slate-400">
                No discounts configured yet
              </div>
            )}
          </div>
        </TabsContent>

        {/* Student Fees Tab */}
        <TabsContent value="fees" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-slate-800">Student Fee Records</h3>
            <div className="flex gap-2">
              <select
                className="px-3 py-2 border rounded-lg text-sm"
                value={feeFilter}
                onChange={(e) => setFeeFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="partial">Partial Paid</option>
                <option value="paid">Fully Paid</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
          </div>

          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3">Student</th>
                    <th className="text-left p-3">Course</th>
                    <th className="text-left p-3">Mode</th>
                    <th className="text-left p-3">Net Fee</th>
                    <th className="text-left p-3">Paid</th>
                    <th className="text-left p-3">Pending</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Inst. 1</th>
                    <th className="text-left p-3">Inst. 2</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFees.map(fee => (
                    <tr key={fee.id} className="border-t hover:bg-slate-50">
                      <td className="p-3">
                        <p className="font-medium">{fee.student_name}</p>
                        <p className="text-xs text-slate-400">{fee.student_email}</p>
                      </td>
                      <td className="p-3">{fee.course_name}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          fee.enrollment_mode === 'online' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'
                        }`}>
                          {fee.enrollment_mode}
                        </span>
                      </td>
                      <td className="p-3">₹{fee.net_fee?.toLocaleString()}</td>
                      <td className="p-3 text-emerald-600 font-medium">₹{fee.total_paid?.toLocaleString()}</td>
                      <td className="p-3 text-amber-600 font-medium">₹{fee.total_pending?.toLocaleString()}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          fee.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                          fee.status === 'partial' ? 'bg-blue-100 text-blue-700' :
                          fee.status === 'overdue' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {fee.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          fee.installment_1_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                          fee.installment_1_status === 'overdue' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {fee.installment_1_status}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          fee.installment_2_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                          fee.installment_2_status === 'overdue' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {fee.installment_2_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredFees.length === 0 && (
                <p className="text-slate-400 text-center py-8">No fee records found</p>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FeeManagement;
